/**
 * YouTube helpers and utilities
 */

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

/**
 * Parse YouTube URL or video ID
 */
export function parseYoutubeVideoId(
  urlOrId: string
): { videoId: string; startSec: number } {
  if (/^[a-zA-Z0-9_-]{11}$/.test(urlOrId)) {
    return { videoId: urlOrId, startSec: 0 };
  }

  try {
    const u = new URL(urlOrId);
    const v = u.searchParams.get("v") || "";
    const t = u.searchParams.get("t") || u.searchParams.get("start") || "";

    let startSec = 0;
    if (t) {
      if (/^\d+(\.\d+)?$/.test(t)) {
        startSec = Number(t);
      } else {
        const h = /(\d+)h/.exec(t)?.[1];
        const m = /(\d+)m/.exec(t)?.[1];
        const s = /(\d+)s/.exec(t)?.[1];
        startSec =
          (h ? Number(h) * 3600 : 0) +
          (m ? Number(m) * 60 : 0) +
          (s ? Number(s) : 0);
      }
    }

    return { videoId: v || urlOrId, startSec };
  } catch {
    return { videoId: urlOrId, startSec: 0 };
  }
}

/**
 * Ensure YouTube IFrame API is loaded
 */
export function ensureYoutubeIframeApi(): Promise<void> {
  if (window.YT && window.YT.Player) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const existing = document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]'
    );
    if (!existing) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      try {
        prev?.();
      } catch {}
      resolve();
    };
  });
}

/**
 * Set playback rate safely (picks closest available rate)
 */
export function setPlaybackRateSafe(player: any, desired: number): void {
  try {
    console.log('[setPlaybackRateSafe] Called with desired:', desired);
    
    if (!player || typeof player.getAvailablePlaybackRates !== 'function') {
      console.error('[setPlaybackRateSafe] Player not ready or missing API methods');
      return;
    }

    const rates: number[] = player.getAvailablePlaybackRates() ?? [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
    console.log('[setPlaybackRateSafe] Available rates:', rates);
    
    if (!rates.length) {
      console.log('[setPlaybackRateSafe] No rates available');
      return;
    }

    let best = rates[0];
    let bestD = Math.abs(best - desired);
    for (const r of rates) {
      const d = Math.abs(r - desired);
      if (d < bestD) {
        best = r;
        bestD = d;
      }
    }
    
    console.log('[setPlaybackRateSafe] Setting rate to:', best, 'for desired:', desired);
    player.setPlaybackRate(best);
    
    // Verify
    setTimeout(() => {
      const actual = player.getPlaybackRate?.();
      console.log('[setPlaybackRateSafe] Verified actual rate:', actual, 'expected:', best);
    }, 100);
  } catch (e) {
    console.error('[setPlaybackRateSafe] Error:', e);
  }
}

/**
 * Sample YouTube current time (median of multiple samples to reduce jitter)
 */
export async function sampleYtMsMedian(player: any): Promise<number> {
  const samples: number[] = [];
  for (let i = 0; i < 5; i++) {
    try {
      const sec = Number(player?.getCurrentTime?.() ?? 0);
      samples.push(sec * 1000);
    } catch {
      samples.push(0);
    }
    await new Promise((r) => setTimeout(r, 40));
  }
  samples.sort((a, b) => a - b);
  return samples[Math.floor(samples.length / 2)] || 0;
}
